"use client";

/**
 * SignalPerformanceView — the specialized "land-on" view for a signal alert.
 *
 * Three sub-tabs:
 *   1. Current Signal   — ONLY the most recently fired positions (grouped by
 *                         exact opened_at timestamp = the latest register run),
 *                         the book-level thesis ("Why these positions"), and
 *                         beginner-proof "How to trade this" instructions.
 *   2. Performance      — hero + stats grid + equity curve (CLOSED trades only).
 *   3. Previous Trades  — one sortable table of every trade, active + closed.
 *
 * Thesis is BOOK-LEVEL ONLY (signal.thesis_md). Positions never carry thesis.
 *
 * Styling contract (user-locked): white text only, emerald accent (no blue),
 * minimal rounding, tight padding, flat layout.
 */

import { useMemo, useState } from "react";
import { useRouter, usePathname, useSearchParams } from "next/navigation";
import type { SignalPerformance, TradePosition } from "@/lib/mock/signal-performance";
import { SignalHero } from "./SignalHero";
import { CurrentTradeCard } from "./CurrentTradeCard";
import { EquityCurveChart } from "./EquityCurveChart";
import { PreviousTradesTable } from "./PreviousTradesTable";
import { Sparkles, Mail, BellRing } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

type SubTab = "current" | "performance";

function pct(n: number) {
  const s = n >= 0 ? "+" : "";
  return `${s}${n.toFixed(1)}%`;
}

function fmtPrice(n: number) {
  return n.toFixed(2);
}

function optionLegLabel(p: TradePosition): string {
  if (p.kind !== "option" || !p.legs?.length) return p.ticker;
  return p.legs
    .map((leg) => {
      const action = leg.action === "sell" ? "sell to open" : "buy to open";
      const right = leg.right === "call" ? "call" : "put";
      return `${action} ${leg.ticker} ${leg.strike}${leg.right === "call" ? "C" : "P"} ${right}`;
    })
    .join(" + ");
}

function MiniStat({ label, value, tone }: { label: string; value: string; tone?: "up" | "down" }) {
  const color = tone === "up" ? "text-emerald-400" : tone === "down" ? "text-red-400" : "text-white";
  return (
    <div className="rounded-sm border border-white/10 bg-black/40 px-3 py-2 flex flex-col gap-0.5">
      <span className="text-[10px] uppercase tracking-widest text-white/70 font-bold">{label}</span>
      <span className={`text-sm font-bold tabular-nums ${color}`}>{value}</span>
    </div>
  );
}

/** Broker steps for one position, derived 1:1 from its registered exit rules. */
function brokerSteps(p: TradePosition): string[] {
  const steps: string[] = [];
  if (p.kind === "option" && p.legs?.length) {
    const expiry = p.expiry || p.legs[0]?.expiry;
    steps.push(`Open the ${expiry ? `${expiry} ` : ""}options chain for ${p.underlying_ticker || p.ticker}.`);
    steps.push(`Enter this as one ${p.strategy_type || "options"} order: ${optionLegLabel(p)}.`);
    steps.push(`Use a limit price near the current net ${p.is_credit ? "credit" : "cost"} shown above ($${fmtPrice(p.current_price)}).`);
    if (p.exit.take_profit != null) {
      steps.push(`Take profit near $${fmtPrice(p.exit.take_profit)} net value.`);
    }
    if (p.exit.stop_loss != null) {
      steps.push(`Stop out near $${fmtPrice(p.exit.stop_loss)} net value.`);
    }
    if (p.exit.time_exit_days != null) {
      steps.push(`If neither level hits, close the position after ${p.exit.time_exit_days} trading day${p.exit.time_exit_days === 1 ? "" : "s"}.`);
    }
    return steps;
  }
  const verb = p.direction === "short" ? "Short" : "Buy";
  steps.push(
    `${verb} ${p.ticker} near $${fmtPrice(p.entry)}${
      p.notional_usd ? ` (this signal sizes it at $${Math.round(p.notional_usd).toLocaleString()})` : ""
    }.`,
  );
  if (p.exit.take_profit != null) {
    steps.push(`Set a limit sell (take profit) at $${fmtPrice(p.exit.take_profit)}.`);
  }
  if (p.exit.stop_loss != null) {
    steps.push(`Set a stop loss at $${fmtPrice(p.exit.stop_loss)}.`);
  } else if (p.exit.trail_pct != null) {
    steps.push(
      `Set a ${Math.round(p.exit.trail_pct * 100)}% trailing stop. It rises as the stock makes new highs, so your gains stay locked in.`,
    );
  }
  if (p.exit.time_exit_days != null) {
    steps.push(`If nothing triggers, close the position ${p.exit.time_exit_days} days after entry.`);
  }
  return steps;
}

export function SignalPerformanceView({
  signal,
  isMock = false,
}: {
  signal: SignalPerformance;
  isMock?: boolean;
}) {
  const [tab, setTab] = useState<SubTab>("current");
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const goToDescription = () => {
    const params = new URLSearchParams(searchParams?.toString() ?? "");
    params.set("tab", "description");
    router.push(`${pathname}?${params.toString()}`, { scroll: false });
  };

  // Current Signal(s) tab shows EVERY currently-open position as a carousel —
  // each one is a live trade the user is holding right now, not just the most
  // recent run. Sorted newest-first so the freshest signal lands first.
  const sortedCurrent = useMemo(() => {
    const runTime = (p: TradePosition): number => {
      const iso = p.registered_at || p.opened_at;
      return iso ? new Date(iso).getTime() : 0;
    };
    const positions = [...signal.current.positions].sort((a, b) => runTime(b) - runTime(a));
    return { ...signal.current, positions };
  }, [signal.current]);

  const perf = signal.performance;
  const rawThesisMd = signal.current.thesis_md || signal.current.thesis;
  // Prefer the Gemini-generated broker steps (Agent A / A2). Falls back to the
  // locally-derived brokerSteps() when the signal has no generated copy yet.
  const howToTradeMd = signal.current.how_to_trade_md;
  // The backend thesis/how-to-trade now always describe ONLY the currently-open
  // book (regenerated on every close), so the frontend renders them as-is — no
  // stale-ticker detection needed.
  const thesisMd = rawThesisMd;
  const shouldUseGeneratedHowToTrade = Boolean(howToTradeMd) && sortedCurrent.kind !== "option";

  return (
    <div className="relative h-full w-full overflow-y-auto bg-background">
      <div className="mx-auto max-w-5xl px-4 sm:px-6 py-4">

        {/* Header & tabs */}
        <div className="mb-5 border-b border-white/10">
          <div className="flex items-center gap-3 mb-3">
            <h1 className="text-2xl font-bold text-white tracking-tight">{signal.name}</h1>
            {isMock && (
              <span className="px-2.5 py-0.5 rounded-sm text-[11px] font-semibold bg-white/10 border border-white/20 text-white">
                Mock
              </span>
            )}
          </div>

          <div className="flex items-center gap-8">
            {([
              ["current", "Current Signal(s)"],
              ["performance", "Performance"],
            ] as const).map(([key, label]) => (
              <button
                key={key}
                onClick={() => setTab(key)}
                className={`pb-2.5 text-sm font-bold transition-all border-b-2 ${
                  tab === key
                    ? "border-emerald-400 text-white"
                    : "border-transparent text-white/60 hover:text-white"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* TAB: CURRENT SIGNAL — every currently-open position (carousel) */}
        {tab === "current" && (
          <div className="flex flex-col gap-5 animate-in fade-in slide-in-from-bottom-2 duration-500">
            <CurrentTradeCard position={sortedCurrent} isMock={isMock} />

            {/* Why these positions — the BOOK-LEVEL thesis, own card */}
            {thesisMd && (
              <section className="rounded-sm border border-white/10 bg-black/40 p-5">
                <div className="flex items-center gap-2.5 mb-4">
                  <Sparkles className="w-5 h-5 text-emerald-400" />
                  <h2 className="text-lg font-bold text-white tracking-tight">Why these positions</h2>
                </div>
                <div className="prose prose-invert max-w-none prose-p:text-[13.5px] prose-p:text-white prose-p:leading-relaxed prose-li:text-[13.5px] prose-li:text-white prose-headings:text-white prose-headings:font-bold prose-headings:tracking-tight prose-h3:text-[15px] prose-strong:text-white prose-strong:font-semibold marker:text-emerald-400">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{thesisMd}</ReactMarkdown>
                </div>
              </section>
            )}

            {/* How to trade this — beginner-proof, own card */}
            <section className="rounded-sm border border-white/10 bg-black/40 p-5">
              <div className="flex items-center justify-between gap-3 mb-4">
                <div className="flex items-center gap-2.5">
                  <BellRing className="w-5 h-5 text-emerald-400" />
                  <h2 className="text-lg font-bold text-white tracking-tight">How to trade this</h2>
                </div>
                <button
                  onClick={goToDescription}
                  className="shrink-0 rounded-sm border border-emerald-500/40 bg-emerald-500/15 px-3 py-1.5 text-[12px] font-bold text-emerald-300 hover:bg-emerald-500/25 transition-colors"
                >
                  Read the full strategy →
                </button>
              </div>

              <p className="text-[13.5px] text-white leading-relaxed mb-3">
                You don&apos;t need to watch anything. Just place the order{sortedCurrent.positions.length > 1 ? "s" : ""} below
                with your broker and we handle the rest. This signal re-checks every position
                every trading day.
              </p>

              {shouldUseGeneratedHowToTrade ? (
                <div className="prose prose-invert max-w-none prose-p:text-[13.5px] prose-p:text-white prose-p:leading-relaxed prose-li:text-[13.5px] prose-li:text-white prose-headings:text-white prose-headings:font-bold prose-headings:tracking-tight prose-h3:text-[15px] prose-strong:text-white prose-strong:font-semibold marker:text-emerald-400">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{howToTradeMd}</ReactMarkdown>
                </div>
              ) : (
                sortedCurrent.positions.map((p) => (
                  <div key={p.id || p.ticker} className="mb-3">
                    {sortedCurrent.positions.length > 1 && (
                      <span className="text-[11px] font-bold uppercase tracking-widest text-emerald-300">
                        {p.name || p.ticker}
                      </span>
                    )}
                    <ol className="mt-1.5 flex flex-col gap-1">
                      {brokerSteps(p).map((step, i) => (
                        <li key={i} className="flex items-baseline gap-2 text-[13.5px] text-white leading-relaxed">
                          <span className="text-[11px] font-bold tabular-nums text-emerald-400 shrink-0">{i + 1}.</span>
                          {step}
                        </li>
                      ))}
                    </ol>
                  </div>
                ))
              )}

              <div className="mt-4 rounded-sm border border-emerald-500/25 bg-emerald-500/[0.07] px-3.5 py-3">
                <p className="text-[13px] text-white leading-relaxed">
                  <Mail className="inline-block w-4 h-4 text-emerald-300 mr-1.5 -translate-y-px" />
                  <span className="font-bold">What to wait for:</span> the moment a position closes,
                  we email you. That includes hitting the profit target, getting stopped out, or
                  reaching the time exit, so you always know exactly when to act. Every closed trade
                  also shows up on this page under Previous Trades. <span className="font-bold">Subscribe to this signal</span> to
                  get these emails plus every new trade the moment it fires.
                </p>
              </div>
            </section>
          </div>
        )}

        {/* TAB: PERFORMANCE — closed trades only (realized track record) */}
        {tab === "performance" && (
          <div className="flex flex-col gap-5 animate-in fade-in slide-in-from-bottom-2 duration-500">
            <SignalHero summary={perf} current={signal.current} />

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
              <MiniStat label="All time" value={pct(perf.all_time_pct)} tone={perf.all_time_pct >= 0 ? "up" : "down"} />
              <MiniStat label="Win rate" value={`${Math.round(perf.win_rate * 100)}%`} />
              <MiniStat label="Avg win" value={pct(perf.avg_win_pct)} tone="up" />
              <MiniStat label="Avg loss" value={pct(perf.avg_loss_pct)} tone="down" />
              <MiniStat label="7 day" value={pct(perf.pct_7d)} tone={perf.pct_7d >= 0 ? "up" : "down"} />
              <MiniStat label="30 day" value={pct(perf.pct_30d)} tone={perf.pct_30d >= 0 ? "up" : "down"} />
              <MiniStat label="Max drawdown" value={pct(perf.max_drawdown_pct)} tone="down" />
              <MiniStat label="Closed trades" value={`${perf.total_trades}`} />
            </div>

            <EquityCurveChart data={signal.equity_curve} />

            {/* Previous Trades — merged under Performance (the closed-trade log) */}
            <div className="mt-1">
              <h2 className="mb-3 text-lg font-bold text-white tracking-tight">Previous Trades</h2>
              <PreviousTradesTable
                trades={signal.previous_trades}
                activePositions={signal.current.positions}
                isMock={isMock}
              />
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
